import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { SessionTimeout } from "@/components/auth/SessionTimeout";

const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Audits = lazy(() => import("./pages/Audits"));
const NewAudit = lazy(() => import("./pages/NewAudit"));
const EditAudit = lazy(() => import("./pages/EditAudit"));
const AuditDetail = lazy(() => import("./pages/AuditDetail"));
const Standards = lazy(() => import("./pages/Standards"));
const StandardDetail = lazy(() => import("./pages/StandardDetail"));
const EditStandard = lazy(() => import("./pages/EditStandard"));
const ChecklistDetail = lazy(() => import("./pages/ChecklistDetail"));
const EditChecklist = lazy(() => import("./pages/EditChecklist"));
const Reports = lazy(() => import("./pages/Reports"));
const ProcessingQueue = lazy(() => import("./pages/ProcessingQueue"));
const NewReport = lazy(() => import("./pages/NewReport"));
const EditReport = lazy(() => import("./pages/EditReport"));
const ReportDetail = lazy(() => import("./pages/ReportDetail"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AIQuality = lazy(() => import("./pages/AIQuality"));
const TimeSavingsAnalytics = lazy(() => import("./pages/TimeSavingsAnalytics"));
const ReportCalendar = lazy(() => import("./pages/ReportCalendar"));
const Collaborators = lazy(() => import("./pages/Collaborators"));
const IdentidadeVisual = lazy(() => import("./pages/IdentidadeVisual"));
const CollaboratorDetail = lazy(() => import("./pages/CollaboratorDetail"));
const EditCollaborator = lazy(() => import("./pages/EditCollaborator"));
const ManagementDashboard = lazy(() => import("./pages/ManagementDashboard"));
const StatusReport = lazy(() => import("./pages/StatusReport"));
const ChecklistGroupDetail = lazy(() => import("./pages/ChecklistGroupDetail"));
const EditChecklistGroup = lazy(() => import("./pages/EditChecklistGroup"));
const Incidents = lazy(() => import("./pages/Incidents"));
const IncidentsOverview = lazy(() => import("./pages/IncidentsOverview"));
const IncidentDetail = lazy(() => import("./pages/IncidentDetail"));
const IncidentParametrizacao = lazy(() => import("./pages/IncidentParametrizacao"));
const ActionPlans = lazy(() => import("./pages/ActionPlans"));
const ActionPlanDetail = lazy(() => import("./pages/ActionPlanDetail"));
const MyTasks = lazy(() => import("./pages/MyTasks"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail"));
const EditCompany = lazy(() => import("./pages/EditCompany"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const EditTeam = lazy(() => import("./pages/EditTeam"));
const EditGroup = lazy(() => import("./pages/EditGroup"));
const GestaoMudanca = lazy(() => import("./pages/GestaoMudanca"));
const GMUDOverview = lazy(() => import("./pages/GMUDOverview"));
const GMUDRelatorios = lazy(() => import("./pages/GMUDRelatorios"));
const GMUDDetail = lazy(() => import("./pages/GMUDDetail"));
const GMUDParametrizacao = lazy(() => import("./pages/GMUDParametrizacao"));
const DocumentosLista = lazy(() => import("./pages/DocumentosLista"));
const DocumentosParametrizacao = lazy(() => import("./pages/DocumentosParametrizacao"));
const DocumentosLixeira = lazy(() => import("./pages/DocumentosLixeira"));
const DocumentosRelatorios = lazy(() => import("./pages/DocumentosRelatorios"));
const DocumentosOverview = lazy(() => import("./pages/DocumentosOverview"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,  // dados ficam "frescos" por 5 min — evita refetch redundante
      gcTime: 10 * 60 * 1000,    // mantém no cache por 10 min após desmontar
      refetchOnWindowFocus: false, // não refaz queries ao focar a janela
    },
  },
});

const RouteFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <SessionTimeout />
        <Suspense fallback={<RouteFallback />}>
          <ErrorBoundary>
          <Routes>
            {/* Rotas públicas */}
            <Route path="/" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Rotas privadas — exigem autenticação */}
            <Route path="/audits" element={<RequireAuth><Audits /></RequireAuth>} />
            <Route path="/audits/new" element={<RequireAuth><NewAudit /></RequireAuth>} />
            <Route path="/audits/:id" element={<RequireAuth><AuditDetail /></RequireAuth>} />
            <Route path="/audits/:id/edit" element={<RequireAuth><EditAudit /></RequireAuth>} />
            <Route path="/standards" element={<RequireAuth><Standards /></RequireAuth>} />
            <Route path="/standards/:id" element={<RequireAuth><StandardDetail /></RequireAuth>} />
            <Route path="/standards/:id/edit" element={<RequireAuth><EditStandard /></RequireAuth>} />
            <Route path="/checklist/:id" element={<RequireAuth><ChecklistDetail /></RequireAuth>} />
            <Route path="/checklist/:id/edit" element={<RequireAuth><EditChecklist /></RequireAuth>} />
            <Route path="/checklist-groups/:id" element={<RequireAuth><ChecklistGroupDetail /></RequireAuth>} />
            <Route path="/checklist-groups/:id/edit" element={<RequireAuth><EditChecklistGroup /></RequireAuth>} />
            <Route path="/reports" element={<RequireAuth><Reports /></RequireAuth>} />
            <Route path="/reports/queue" element={<RequireAuth><ProcessingQueue /></RequireAuth>} />
            <Route path="/reports/new" element={<RequireAuth><NewReport /></RequireAuth>} />
            <Route path="/reports/:id" element={<RequireAuth><ReportDetail /></RequireAuth>} />
            <Route path="/reports/:id/edit" element={<RequireAuth><EditReport /></RequireAuth>} />
            <Route path="/users" element={<RequireAuth><UserManagement /></RequireAuth>} />
            <Route path="/ai-quality" element={<RequireAuth><AIQuality /></RequireAuth>} />
            <Route path="/time-savings" element={<RequireAuth><TimeSavingsAnalytics /></RequireAuth>} />
            <Route path="/calendar" element={<RequireAuth><ReportCalendar /></RequireAuth>} />
            <Route path="/collaborators" element={<RequireAuth><Collaborators /></RequireAuth>} />
            <Route path="/configuracoes/identidade-visual" element={<RequireAuth><IdentidadeVisual /></RequireAuth>} />
            <Route path="/collaborators/:id" element={<RequireAuth><CollaboratorDetail /></RequireAuth>} />
            <Route path="/collaborators/:id/edit" element={<RequireAuth><EditCollaborator /></RequireAuth>} />
            <Route path="/management" element={<RequireAuth><ManagementDashboard /></RequireAuth>} />
            <Route path="/status-report" element={<RequireAuth><StatusReport /></RequireAuth>} />
            <Route path="/incidents-overview" element={<RequireAuth><IncidentsOverview /></RequireAuth>} />
            <Route path="/incidents" element={<RequireAuth><Incidents /></RequireAuth>} />
            <Route path="/incidents/parametrizacao" element={<RequireAuth><IncidentParametrizacao /></RequireAuth>} />
            <Route path="/incidents/:id" element={<RequireAuth><IncidentDetail /></RequireAuth>} />
            {/* Edição virou modal no detalhe; rota antiga só redireciona. */}
            <Route path="/incidents/:id/edit" element={<Navigate to=".." replace />} />
            <Route path="/action-plans" element={<RequireAuth><ActionPlans /></RequireAuth>} />
            <Route path="/action-plans/:id" element={<RequireAuth><ActionPlanDetail /></RequireAuth>} />
            <Route path="/my-tasks" element={<RequireAuth><MyTasks /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
            <Route path="/companies" element={<RequireAuth><Companies /></RequireAuth>} />
            <Route path="/companies/:id" element={<RequireAuth><CompanyDetail /></RequireAuth>} />
            <Route path="/companies/:id/edit" element={<RequireAuth><EditCompany /></RequireAuth>} />
            <Route path="/teams/:id" element={<RequireAuth><TeamDetail /></RequireAuth>} />
            <Route path="/teams/:id/edit" element={<RequireAuth><EditTeam /></RequireAuth>} />
            <Route path="/groups/:id/edit" element={<RequireAuth><EditGroup /></RequireAuth>} />
            {/* Gestão de Mudanças */}
            <Route path="/documentos" element={<RequireAuth><DocumentosOverview /></RequireAuth>} />
            <Route path="/documentos/lista" element={<RequireAuth><DocumentosLista /></RequireAuth>} />
            <Route path="/documentos/parametrizacao" element={<RequireAuth><DocumentosParametrizacao /></RequireAuth>} />
            <Route path="/documentos/lixeira" element={<RequireAuth><DocumentosLixeira /></RequireAuth>} />
            <Route path="/documentos/relatorios" element={<RequireAuth><DocumentosRelatorios /></RequireAuth>} />
            <Route path="/gestao-mudanca" element={<RequireAuth><GMUDOverview /></RequireAuth>} />
            <Route path="/gestao-mudanca/lista" element={<RequireAuth><GestaoMudanca /></RequireAuth>} />
            <Route path="/gestao-mudanca/relatorios" element={<RequireAuth><GMUDRelatorios /></RequireAuth>} />
            <Route path="/gestao-mudanca/parametrizacao" element={<RequireAuth><GMUDParametrizacao /></RequireAuth>} />
            <Route path="/gestao-mudanca/:id" element={<RequireAuth><GMUDDetail /></RequireAuth>} />
            {/* A edição da GMUD acontece no próprio detalhe, etapa a etapa. */}
            <Route path="/gestao-mudanca/:id/editar" element={<RequireAuth><GMUDDetail /></RequireAuth>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
